// Backgammon (Tryktrak) renderer on generated art: backgammon_board.webp with
// measured point geometry, checker/dice sprites, a central bar (the art's
// hinge strip) and per-colour off trays (the art's right-side tray). Pure view — it never talks to the
// network; it calls back `onMove({from, to})` with `from` in 0-23|"bar" and
// `to` in 0-23|"off", matching the engine's sub-move shape 1:1 (Task 2:
// backend/app/game/engines/backgammon.py). The engine ships NO board array —
// everything comes from `state` via `extras()` (points/bar/off/dice/used/
// opening/skipped) plus the framework's own `state.turn`.
//
// Orientation: like checkers/chess, MY colour's home is ALWAYS bottom-right.
// The engine's home ranges are fixed in absolute point numbers (0-5 = 'w'
// home, 18-23 = 'b' home) — see dispToPoint() below for the exact display
// mapping per colour (also covers spectators, who get the 'w' view).
//
// Selection: two taps, closest in spirit to chessRenderer.js's from/to picker
// — tap a slot that is some legal move's `from` (gold ring; legal `to`s for
// it get green rings), tap a highlighted `to` to commit, tap the selection
// again to deselect, tap a different legal `from` to re-select. Because the
// engine's `state.turn` only flips once ALL dice are spent/dead, several
// consecutive `update()` calls arrive with `interactive: true` for the same
// player mid-turn (one call per sub-move) — a fresh state always carries a
// fresh `legalMoves`, so selection is reset on every `update()` call, never
// carried across state transitions.

const W_FRAC = 1.3;

// Measured geometry of backgammon_board.webp (1536×1024 source; triangle bases
// and trays traced 2026-07-11). Fractions of the source edges — display
// coordinates are these times the drawn board size.
const SRC_W = 1536, SRC_H = 1024;
// display column d (0-11, left->right) -> triangle centre x (source px).
const COL_XS = [229, 318.5, 406.5, 496.5, 585.5, 675.5, 857, 949, 1038, 1127.5, 1216, 1307.5];
const TRI_W = 89 / SRC_W;            // triangle base width
const FIELD_TOP = 50 / SRC_H, FIELD_BOTTOM = 974 / SRC_H;
const TRI_LEN = 320 / SRC_H;         // base -> apex length
const BAR_CX = 769.5 / SRC_W, BAR_WF = 111 / SRC_W;   // hinge strip = the bar
const TRAY_CX = 1483 / SRC_W, TRAY_WF = 66 / SRC_W;   // right-side tray = off

const CHECKER_TEX = { w: 'backgammon_checker_w', b: 'backgammon_checker_b' };
const SEL_GOLD = 0xffd166, SEL_GREEN = 0x3ad07a;

function diceLabel(arr) {
  if (!arr || arr.length === 0) return '';
  if (arr.length === 4) return `${arr[0]}×4`;
  return `${arr[0]}-${arr[1]}`;
}

export function createBackgammonRenderer(scene, { x, y, size, myColor, onMove }) {
  const W = size * W_FRAC, H = W * (SRC_H / SRC_W);
  const left = x - W / 2, top = y - H / 2;
  const innerTop = top + FIELD_TOP * H, innerBottom = top + FIELD_BOTTOM * H;
  const tw = TRI_W * W;
  const th = TRI_LEN * H;
  const BAR_W = BAR_WF * W, TRAY_W = TRAY_WF * W;
  const barCenterX = left + BAR_CX * W;
  const trayCenterX = left + TRAY_CX * W;
  const midY = (innerTop + innerBottom) / 2;
  const r = tw * 0.44;

  // display column d (0-11, left->right within a row) -> world x, from the
  // measured triangle centres (the bar sits between d=5 and d=6).
  const colX = (d) => left + (COL_XS[d] / SRC_W) * W;
  function cellGeom(row, d) {
    const cx = colX(d);
    return row === 'bottom'
      ? { cx, baseY: innerBottom, apexY: innerBottom - th, cyMid: innerBottom - th / 2 }
      : { cx, baseY: innerTop, apexY: innerTop + th, cyMid: innerTop + th / 2 };
  }

  let color = myColor ?? null;                 // my colour, or null for spectators
  let bottomColor = color === 'b' ? 'b' : 'w';  // 'w' also covers spectators (brief)
  const otherColor = () => (bottomColor === 'w' ? 'b' : 'w');

  // display mapping (my home ALWAYS bottom-right) — see module header.
  function dispToPoint(row, d) {
    if (bottomColor === 'w') return row === 'bottom' ? 11 - d : 12 + d;
    return row === 'bottom' ? 12 + d : 11 - d;
  }
  // inverse of dispToPoint, for locating a selected/target point's geometry.
  function pointCell(p) {
    if (bottomColor === 'w') return p <= 11 ? { row: 'bottom', d: 11 - p } : { row: 'top', d: p - 12 };
    return p >= 12 ? { row: 'bottom', d: p - 12 } : { row: 'top', d: 11 - p };
  }

  let points = new Array(24).fill(0);
  let bar = { w: 0, b: 0 };
  let off = { w: 0, b: 0 };
  let dice = [];
  let used = [];
  let skipped = null;
  let turn = null;
  let legalMoves = [];             // [{from, to}] — from: 0-23|'bar', to: 0-23|'off'
  let interactive = false;
  let selection = null;            // 0-23 | 'bar' | null (never 'off' — never a legal `from`)

  const container = scene.add.container(0, 0).setDepth(10);

  // Board art (triangles, bar hinge, trays are all IN the image — overlays
  // below only add checkers/dice/highlights at the measured positions).
  const boardImg = scene.add.image(x, y, 'backgammon_board').setDisplaySize(W, H);

  const highlights = scene.add.graphics();
  const pieceLayer = scene.add.container(0, 0);
  const diceLayer = scene.add.container(0, 0);
  const banner = scene.add.text(x, top - 26, '', {
    fontFamily: 'system-ui', fontSize: '22px', color: '#ffd166', fontStyle: 'bold',
  }).setOrigin(0.5).setVisible(false);
  container.add([boardImg, highlights, pieceLayer, diceLayer, banner]);

  // --- selection helpers -----------------------------------------------------
  const isFrom = (slot) => legalMoves.some((m) => m.from === slot);
  const targetsFor = (from) => legalMoves.filter((m) => m.from === from).map((m) => m.to);

  function onSlotTap(slot) {
    if (!interactive) return;
    if (selection == null) {
      if (isFrom(slot)) { selection = slot; redraw(); }
      return;
    }
    if (slot === selection) { selection = null; redraw(); return; }   // tap again = deselect
    if (targetsFor(selection).includes(slot)) {
      const from = selection;
      selection = null;
      onMove({ from, to: slot });
      return;
    }
    // not a legal target for the current selection — a fresh legal `from`
    // re-selects, anything else clears the selection.
    selection = isFrom(slot) ? slot : null;
    redraw();
  }

  // --- zones (pointerup only) -------------------------------------------------
  // 24 point zones (row/d baked in — physical geometry never changes; the
  // POINT it maps to is resolved at tap time via dispToPoint, so a colour
  // change from the scene still routes taps correctly), one bar zone, one
  // zone for MY off tray (bearing off always targets my own tray — the
  // opponent's tray is display-only).
  const zones = [];
  for (const row of ['bottom', 'top']) {
    for (let d = 0; d < 12; d++) {
      const { cx, cyMid } = cellGeom(row, d);
      const z = scene.add.zone(cx, cyMid, tw, th * 1.15).setInteractive({ useHandCursor: true });
      z.on('pointerup', () => onSlotTap(dispToPoint(row, d)));
      zones.push(z);
      container.add(z);
    }
  }
  const barZone = scene.add.zone(barCenterX, midY, BAR_W, innerBottom - innerTop)
    .setInteractive({ useHandCursor: true });
  barZone.on('pointerup', () => onSlotTap('bar'));
  zones.push(barZone);
  container.add(barZone);

  const myTrayCy = (midY + innerBottom) / 2;
  const offZone = scene.add.zone(trayCenterX, myTrayCy, TRAY_W, innerBottom - midY)
    .setInteractive({ useHandCursor: true });
  offZone.on('pointerup', () => onSlotTap('off'));
  zones.push(offZone);
  container.add(offZone);

  // --- drawing -----------------------------------------------------------------
  function drawDisc(cx, cy, colorKey) {
    pieceLayer.add(scene.add.image(cx, cy, CHECKER_TEX[colorKey]).setDisplaySize(r * 2, r * 2));
  }
  function drawCountText(cx, cy, n) {
    pieceLayer.add(scene.add.text(cx, cy, `${n}`, {
      fontFamily: 'system-ui', fontSize: '15px', color: '#ffffff', fontStyle: 'bold',
    }).setOrigin(0.5).setStroke('#000000', 3));
  }
  // Stacks from `baseY` toward `apexY` (the row/half's own centre); max 5
  // discs drawn, the 5th carries the exact count when there are more.
  function drawStack(cx, baseY, apexY, colorKey, count) {
    if (count <= 0) return;
    const dir = apexY < baseY ? -1 : 1;
    const span = Math.abs(apexY - baseY);
    const spacing = Math.min(r * 1.9, span / 5);
    const shown = Math.min(count, 5);
    for (let i = 0; i < shown; i++) {
      const cy = baseY + dir * (r + 4) + dir * spacing * i;
      drawDisc(cx, cy, colorKey);
      if (i === shown - 1 && count > shown) drawCountText(cx, cy, count);
    }
  }

  // Borne-off checkers stacked flat in the art's right tray, one thin sprite
  // per checker (capped at 15), from the tray half's outer edge toward the
  // shared centre line — the same outer->centre convention as drawStack.
  function drawTray(cx, outerY, innerY, colorKey, count) {
    const cellH = Math.abs(innerY - outerY);
    const dir = innerY < outerY ? -1 : 1;
    const w = TRAY_W - 10;
    const barH = Math.max(4, (cellH - 8) / 15 - 1.5);
    const n = Math.min(count, 15);
    for (let i = 0; i < n; i++) {
      const yy = outerY + dir * (6 + i * ((cellH - 8) / 15));
      pieceLayer.add(scene.add.image(cx, yy + dir * barH / 2, CHECKER_TEX[colorKey])
        .setDisplaySize(w, barH * 2.4).setAlpha(0.95));
    }
    pieceLayer.add(scene.add.text(cx, (outerY + innerY) / 2, `${count}`, {
      fontFamily: 'system-ui', fontSize: '17px', color: '#ffffff', fontStyle: 'bold',
    }).setOrigin(0.5).setStroke('#000000', 3).setDepth(1));
  }

  function highlightSlot(slot, col) {
    let cx, cy, w, h;
    if (slot === 'bar') {
      cx = barCenterX; cy = midY; w = BAR_W; h = innerBottom - innerTop;
    } else if (slot === 'off') {
      cx = trayCenterX; cy = myTrayCy; w = TRAY_W; h = innerBottom - midY;
    } else {
      const { row, d } = pointCell(slot);
      const g = cellGeom(row, d);
      cx = g.cx; cy = g.cyMid; w = tw; h = th * 1.15;
    }
    highlights.fillStyle(col, 0.28).fillRect(cx - w / 2, cy - h / 2, w, h);
    highlights.lineStyle(3, col, 0.9).strokeRect(cx - w / 2, cy - h / 2, w, h);
  }

  function drawDice() {
    diceLayer.removeAll(true);
    const glyphs = [...used, ...dice].slice(0, 4);
    if (!glyphs.length || !turn) return;
    // "mover's side of the bar top": the dice sit in the bar column, on
    // whichever vertical half belongs to the current mover, anchored at the
    // higher (smaller-y) edge of THAT half — for the opponent (upper half)
    // that is literally the top of the board; for me (lower half) that is
    // the edge nearest the centre line, keeping the glyphs clear of the bar
    // checker stacks (which grow from the OUTER edges toward the centre).
    const moverIsBottom = turn === bottomColor;
    const cy = moverIsBottom ? midY + 24 : innerTop + 24;
    const step = 38, die = 32;
    let startX = barCenterX - ((glyphs.length - 1) * step) / 2;
    glyphs.forEach((v, i) => {
      diceLayer.add(scene.add.image(startX + i * step, cy, `backgammon_die_${v}`)
        .setDisplaySize(die, die).setAlpha(i < used.length ? 0.35 : 1));
    });
  }

  function bannerText() {
    if (!color) return null;   // every message below is phrased at "you" — no banner for spectators
    if (skipped) {
      return skipped.turn === color
        ? `Brak ruchu! Kości: ${diceLabel(skipped.dice)}`
        : `Przeciwnik nie miał ruchu (${diceLabel(skipped.dice)})`;
    }
    if (interactive && dice.length > 0 && used.length === 0) {
      return dice.length === 4 ? `Dublet! ${dice[0]}×4` : `Twój rzut: ${dice[0]}-${dice[1]}`;
    }
    return null;
  }

  function redraw() {
    highlights.clear();
    pieceLayer.removeAll(true);

    for (const row of ['bottom', 'top']) {
      for (let d = 0; d < 12; d++) {
        const p = dispToPoint(row, d);
        const v = points[p] ?? 0;
        if (v === 0) continue;
        const { cx, baseY, apexY } = cellGeom(row, d);
        drawStack(cx, baseY, apexY, v > 0 ? 'w' : 'b', Math.abs(v));
      }
    }
    // bar: mine in the lower half (stacking up toward centre), opponent's in
    // the upper half (stacking down toward centre) — mirrors the point rows.
    drawStack(barCenterX, innerBottom, midY, bottomColor, bar[bottomColor] ?? 0);
    drawStack(barCenterX, innerTop, midY, otherColor(), bar[otherColor()] ?? 0);
    // off trays: mine bottom-right, opponent's top-right.
    drawTray(trayCenterX, innerBottom, midY, bottomColor, off[bottomColor] ?? 0);
    drawTray(trayCenterX, innerTop, midY, otherColor(), off[otherColor()] ?? 0);

    if (interactive && selection != null) {
      highlightSlot(selection, SEL_GOLD);
      for (const t of targetsFor(selection)) highlightSlot(t, SEL_GREEN);
    }

    drawDice();
    const txt = bannerText();
    banner.setText(txt ?? '').setVisible(!!txt);
  }

  function update({ legalMoves: lm, interactive: on, myColor: mc, state: st }) {
    if (mc !== undefined) { color = mc ?? null; bottomColor = color === 'b' ? 'b' : 'w'; }
    if (st) {
      if (st.points) points = st.points.slice();
      if (st.bar) bar = { ...st.bar };
      if (st.off) off = { ...st.off };
      dice = st.dice ? st.dice.slice() : [];
      used = st.used ? st.used.slice() : [];
      skipped = st.skipped ?? null;
      turn = st.turn ?? null;
    }
    legalMoves = lm || [];
    interactive = !!on;
    selection = null;   // a fresh state (even mid-turn, same player) always resets the pick
    redraw();
  }

  // Panel hook: bar + home tally per colour (the generic captured counter is
  // meaningless here — backgammon has no captured-piece concept).
  function capturedGlyphs(st, panelColor) {
    if (!st?.bar || !st?.off) return null;
    return `Bar: ${st.bar[panelColor]} · Dom: ${st.off[panelColor]}`;
  }

  // Sfx key for a state transition, priority hit > roll > move. A fresh roll
  // always arrives IN THE SAME broadcast as the turn-ending sub-move or skip
  // (match_flow resolves chance eagerly, so no broadcast ever shows empty
  // dice mid-game — final-review probe: 0/934 transitions). The roll
  // therefore keys off the TURN FLIP (the next player's dice just landed),
  // plus the opening resolving into the first roll.
  function moveSfx(prevState, nextState) {
    if (!prevState || !nextState) return 'backgammon_move';
    const mover = prevState.turn;
    const opp = mover === 'w' ? 'b' : 'w';
    if ((nextState.bar?.[opp] ?? 0) > (prevState.bar?.[opp] ?? 0)) return 'backgammon_hit';
    if (nextState.turn !== prevState.turn) return 'backgammon_roll';
    if (prevState.opening && !nextState.opening) return 'backgammon_roll';
    return 'backgammon_move';
  }

  function clearSelection() {
    if (selection != null) { selection = null; redraw(); }
  }

  function destroy() {
    zones.forEach((z) => z.destroy());
    container.destroy(true);
  }

  return {
    update, clearSelection, destroy, capturedGlyphs, moveSfx,
    // e2e hook: drive the exact tap->onMove path without a real pointer.
    tapSlot: (slot) => onSlotTap(slot),
  };
}
